/**
 * Content moderation utilities for filtering user-generated content
 * Implements App Store requirement 1.2 for filtering objectionable material
 */

// Basic profanity filter - in production, you'd want a more comprehensive solution
const PROFANITY_PATTERNS = [
	// Common profanity patterns (add more as needed)
	/\b(fuck|shit|damn|ass(?:hole)?|bitch|cunt|dick|piss|bastard)\b/gi,
	// Harassment patterns
	/\b(kill\s+yourself|kys|suicide|die)\b/gi,
	// Hate speech patterns
	/\b(n[i1]gg[ae]r|f[a4]gg[o0]t|r[e3]t[a4]rd)\b/gi,
	// Sexual content
	/\b(porn|xxx|sex|nude|naked|masturbat)\b/gi,
];

// Spam patterns
const SPAM_PATTERNS = [
	// Repeated characters
	/(.)\1{4,}/g,
	// URLs (basic detection)
	/https?:\/\/[^\s]+/gi,
	// Email addresses
	/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi,
];

export interface ModerationResult {
	isAppropriate: boolean;
	flaggedFor: string[];
	filteredContent?: string;
	severity: "low" | "medium" | "high";
	moderationStatus: "pending" | "approved" | "flagged" | "rejected";
}

/**
 * Moderate user-generated content for appropriateness
 */
export function moderateContent(content: string): ModerationResult {
	if (!content || typeof content !== "string") {
		return {
			isAppropriate: true,
			flaggedFor: [],
			severity: "low",
			moderationStatus: "approved",
		};
	}

	const flaggedFor: string[] = [];
	let severity: "low" | "medium" | "high" = "low";

	// Check for profanity
	for (const pattern of PROFANITY_PATTERNS) {
		if (pattern.test(content)) {
			flaggedFor.push("profanity");
			severity = "high";
			break;
		}
	}

	// Check for spam
	for (const pattern of SPAM_PATTERNS) {
		if (pattern.test(content)) {
			flaggedFor.push("spam");
			if (severity === "low") severity = "medium";
			break;
		}
	}

	// Check for excessive caps (yelling)
	const capsRatio = (content.match(/[A-Z]/g)?.length || 0) / content.length;
	if (capsRatio > 0.7 && content.length > 10) {
		flaggedFor.push("excessive_caps");
		if (severity === "low") severity = "medium";
	}

	// Check for very short/low quality content
	if (content.trim().length < 3) {
		flaggedFor.push("low_quality");
		if (severity === "low") severity = "medium";
	}

	const isAppropriate = flaggedFor.length === 0;

	// Determine moderation status
	let moderationStatus: "pending" | "approved" | "flagged" | "rejected";
	if (
		shouldAutoReject({
			isAppropriate,
			flaggedFor,
			severity,
			moderationStatus: "pending",
		})
	) {
		moderationStatus = "rejected";
	} else if (
		needsManualReview({
			isAppropriate,
			flaggedFor,
			severity,
			moderationStatus: "pending",
		})
	) {
		moderationStatus = "flagged";
	} else if (isAppropriate) {
		moderationStatus = "approved";
	} else {
		moderationStatus = "pending";
	}

	return {
		isAppropriate,
		flaggedFor,
		filteredContent: isAppropriate ? content : filterProfanity(content),
		severity,
		moderationStatus,
	};
}

/**
 * Filter profanity from content by replacing with asterisks
 */
function filterProfanity(content: string): string {
	let filtered = content;

	for (const pattern of PROFANITY_PATTERNS) {
		filtered = filtered.replace(
			pattern,
			(match) =>
				match.charAt(0) +
				"*".repeat(match.length - 2) +
				match.charAt(match.length - 1),
		);
	}

	return filtered;
}

/**
 * Check if content should be auto-rejected
 */
export function shouldAutoReject(moderationResult: ModerationResult): boolean {
	return (
		moderationResult.severity === "high" &&
		(moderationResult.flaggedFor.includes("profanity") ||
			moderationResult.flaggedFor.includes("hate_speech"))
	);
}

/**
 * Check if content needs manual review
 */
export function needsManualReview(moderationResult: ModerationResult): boolean {
	return (
		moderationResult.severity === "medium" ||
		(moderationResult.severity === "high" &&
			!shouldAutoReject(moderationResult))
	);
}